import { getTotalSteps } from './utils';
import { EXECUTION_STATUS } from './hooks/useReplayExecution';
import { countSkippedReplaySteps } from './utils/replayProgressDisplay';
import { resolveRecordingStepStatus } from '../../features/automationStudioNew/recordingPlanMarkers';

function countRecordingStepOutcome(step) {
    const resolved = resolveRecordingStepStatus(step);
    if (resolved === 'failed' || resolved === 'failed_by_evaluation') return 'failed';
    if (resolved === 'completed' || step?.status === 'success') return 'success';
    if (step?.status === 'failed') return 'failed';
    return null;
}

/**
 * Shared execution totals for ExecutionView banner + CompletedExecutionView (no duplicate math).
 */
export function computeExecutionSummaryStats({
    instructions = [],
    replayProgressMap = {},
    replayExecutionResults = null,
    executionResults = null,
    overallStatus = null,
    automationError = null,
}) {
    const totalSteps = getTotalSteps(instructions);
    const hasReplayProgress = replayProgressMap && Object.keys(replayProgressMap).length > 0;

    let successSteps;
    let failedSteps;
    let skippedSteps = 0;

    if (hasReplayProgress) {
        const statuses = Object.values(replayProgressMap);
        successSteps = statuses.filter((s) => s === EXECUTION_STATUS.PASSED).length;
        failedSteps = statuses.filter((s) => s === EXECUTION_STATUS.FAILED).length;
        skippedSteps = countSkippedReplaySteps(replayProgressMap);
    } else {
        successSteps = instructions.reduce((sum, inst) => {
            if (!inst.aiSteps) return sum;
            return (
                sum +
                inst.aiSteps.filter((step) => countRecordingStepOutcome(step) === 'success').length
            );
        }, 0);
        failedSteps = instructions.reduce((sum, inst) => {
            if (!inst.aiSteps) return sum;
            return (
                sum +
                inst.aiSteps.filter((step) => countRecordingStepOutcome(step) === 'failed').length
            );
        }, 0);
        // User-stopped recording: pending / never-run steps were not attempted.
        if (overallStatus?.status === 'partial') {
            skippedSteps = Math.max(0, totalSteps - successSteps - failedSteps);
        }
    }

    const { status: overallStatusValue } = overallStatus || {};
    const isPartialOutcome = overallStatusValue === 'partial';
    // Run-level failure (e.g. pre-navigation) may not mark any step "failed" while step rows still
    // look successful — align counts with the failed outcome so the banner matches the message.
    const runFailedWithoutStepFailure =
        !hasReplayProgress &&
        totalSteps > 0 &&
        failedSteps === 0 &&
        successSteps === totalSteps &&
        (automationError || overallStatusValue === 'failed');
    if (runFailedWithoutStepFailure) {
        failedSteps = 1;
        successSteps = Math.max(0, totalSteps - 1);
    }

    const replayDurationFromStepDetails = (() => {
        const stepDetails = replayExecutionResults?.stepDetails;
        if (!stepDetails || typeof stepDetails !== 'object') return undefined;
        if (Object.keys(stepDetails).length === 0) return undefined;

        const getStepDurationMs = (detail) => {
            const ms = detail?.duration ?? detail?.result?.duration ?? detail?.result?.result?.duration;
            const n = typeof ms === 'number' ? ms : Number(ms);
            return Number.isFinite(n) ? n : 0;
        };

        return Object.values(stepDetails).reduce((sum, detail) => sum + getStepDurationMs(detail), 0);
    })();

    const totalDuration =
        executionResults?.totalDuration ??
        replayExecutionResults?.totalDuration ??
        replayDurationFromStepDetails ??
        instructions.reduce((sum, inst) => {
            if (!inst.aiSteps) return sum;
            return sum + inst.aiSteps.reduce((s, step) => s + (step.duration || 0), 0);
        }, 0);

    const attemptedSteps = successSteps + failedSteps;
    const hasFailures = isPartialOutcome
        ? false
        : automationError
        ? true
        : overallStatusValue
          ? overallStatusValue === 'failed'
          : failedSteps > 0;
    const allSuccess = isPartialOutcome
        ? false
        : automationError
        ? false
        : overallStatusValue
          ? overallStatusValue === 'passed'
          : attemptedSteps > 0 && skippedSteps === 0 && successSteps === attemptedSteps;

    return {
        totalSteps,
        successSteps,
        failedSteps,
        skippedSteps,
        attemptedSteps,
        totalDuration,
        hasReplayProgress,
        hasFailures,
        allSuccess,
    };
}

export function hasExecutionResultsUI({
    testCompleted = false,
    instructions = [],
    replayProgressMap = {},
    automationError = null,
    replayExecutionResults = null,
}) {
    const totalSteps = getTotalSteps(instructions);
    return (
        testCompleted ||
        totalSteps > 0 ||
        (replayProgressMap && Object.keys(replayProgressMap).length > 0) ||
        !!automationError ||
        replayExecutionResults != null
    );
}
